import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as cuisinesApi from './cuisines.api';

import type { CreateCuisineRequest, CuisineListQuery, UpdateCuisineRequest } from '@utazzo/shared';

const QK = {
  list: (query: CuisineListQuery) => ['cuisines', 'list', query] as const,
  detail: (id: string) => ['cuisines', 'detail', id] as const,
};

export const useCuisinesList = (query: CuisineListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => cuisinesApi.listCuisines(query),
    staleTime: 30_000,
  });

export const useCreateCuisine = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCuisineRequest) => cuisinesApi.createCuisine(payload),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['cuisines', 'list'] });
      toast.success(`Added ${c.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add cuisine'),
  });
};

export const useUpdateCuisine = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCuisineRequest }) =>
      cuisinesApi.updateCuisine(id, payload),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['cuisines', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(c.id) });
      toast.success('Cuisine updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update cuisine'),
  });
};

export const useDeleteCuisine = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cuisinesApi.deleteCuisine(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cuisines', 'list'] });
      toast.success('Cuisine deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete cuisine'),
  });
};

export const useBulkDeleteCuisines = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => cuisinesApi.bulkDeleteCuisines(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cuisines'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete cuisines'),
  });
};

export const useBulkSetCuisinesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      cuisinesApi.bulkSetCuisinesStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cuisines'] }),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update cuisine status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
